import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Error } from "../common/Error";
import { ReactComponent as MicIcon } from "../../assets/icons/microphone.svg";
import { AISearchActionCreator } from "../../store/AISearch/actions";
import {
  selectAISearchData,
  selectAISearchError,
  selectAISearchFilters,
  selectAISearchLoading,
  selectAISearchMessage,
} from "../../store/AISearch/selectors";
import { selectUserInfo } from "../../store/selectors";
import { useSpeechRecognition } from "./useSpeechRecognition";
import { HousingCard } from "./components/HousingCard";
import { VariantsDrawer } from "./components/VariantsDrawer";
import { SelectionView } from "./components/SelectionView";
import type { AISearchPropsTypes, HousingItem, SelectedVariant } from "./types";
import "./styles.css";

const MAX_SELECTED = 3;
const SUGGESTIONS = [
  "Двухкомнатная до 8 млн рядом с метро",
  "Студия с рассрочкой на 2 года",
  "Семейная квартира в сданном ЖК",
];

function useInView<T extends Element>(rootMargin = "0px") {
  const [node, setNode] = useState<T | null>(null);
  const [inView, setInView] = useState(false);
  const ref = useCallback((element: T | null) => setNode(element), []);

  useEffect(() => {
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { root: null, rootMargin, threshold: 0 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, rootMargin]);

  return [ref, inView] as const;
}

type Props = AISearchPropsTypes & {
  saveHousingsCollection: (args: { data: any; userId?: string | number | null }) => any;
  user?: any;
};

export const _AISearch: React.FC<Props> = ({
  fetchAISearchResults,
  saveHousingsCollection,
  loading,
  error,
  user,
}) => {
  const [query, setQuery] = useState("");
  const [activeView, setActiveView] = useState<"all" | "picked">("all");
  const [results, setResults] = useState<HousingItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selected, setSelected] = useState<SelectedVariant[]>([]);
  const [activeHousing, setActiveHousing] = useState<HousingItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>();
  const [savedLink, setSavedLink] = useState<string | undefined>();
  const [copied, setCopied] = useState(false);
  const perPage = 5;

  const selectedIds = useMemo(() => new Set(selected.map((item) => item.id)), [selected]);
  const selectedVariantsByHousing = useMemo(() => {
    const counts = new Map<string | number, number>();
    selected.forEach((item) => {
      counts.set(item.housingId, (counts.get(item.housingId) ?? 0) + 1);
    });
    return counts;
  }, [selected]);
  const disabledByLimit = selected.length >= MAX_SELECTED;
  const list = useMemo(() => results, [results]);

  const doFetch = useCallback(async (value: string, nextPage: number, isLoadMore = false) => {
    try {
      if (isLoadMore) setLoadingMore(true);
      const action: any = await fetchAISearchResults({ query: value, page: nextPage, perPage });
      const payload = action?.payload;
      const items = Array.isArray(payload)
        ? payload
        : payload?.items ?? payload?.data ?? payload?.results ?? [];
      const safeItems = Array.isArray(items) ? items : [];
      const pagination = payload?.pagination ?? payload?.data?.pagination;
      const hasNextPage =
        typeof pagination?.page === "number" && typeof pagination?.totalPages === "number"
          ? pagination.page < pagination.totalPages
          : safeItems.length === perPage;

      setResults((current) => (nextPage === 1 ? safeItems : [...current, ...safeItems]));
      setPage(nextPage);
      setHasMore(hasNextPage);
    } finally {
      if (isLoadMore) setLoadingMore(false);
    }
  }, [fetchAISearchResults]);

  const handleSearch = useCallback((value = query) => {
    const normalizedValue = value.trim();
    if (!normalizedValue) return;
    setQuery(normalizedValue);
    setActiveView("all");
    setHasSearched(true);
    setResults([]);
    setPage(1);
    setHasMore(false);
    doFetch(normalizedValue, 1);
  }, [doFetch, query]);

  const { supported, isListening, transcript, start, stop, setTranscript } = useSpeechRecognition((finalText) => {
    setQuery(finalText);
    handleSearch(finalText);
    stop();
  });

  const onVoiceToggle = useCallback(() => {
    if (!supported) {
      alert("Ваш браузер не поддерживает голосовой ввод");
      return;
    }
    if (isListening) stop();
    else {
      setTranscript("");
      start();
    }
  }, [isListening, setTranscript, start, stop, supported]);

  useEffect(() => {
    if (activeView === "picked" && isListening) stop();
  }, [activeView, isListening, stop]);

  const invalidateSavedLink = useCallback(() => {
    setSavedLink(undefined);
    setCopied(false);
    setSaveError(undefined);
  }, []);

  const onToggleVariant = useCallback((variant: SelectedVariant) => {
    invalidateSavedLink();
    setSelected((current) => {
      if (current.some((item) => item.id === variant.id)) {
        return current.filter((item) => item.id !== variant.id);
      }
      return current.length >= MAX_SELECTED ? current : [...current, variant];
    });
  }, [invalidateSavedLink]);

  const removeVariant = useCallback((id: string) => {
    invalidateSavedLink();
    setSelected((current) => current.filter((item) => item.id !== id));
  }, [invalidateSavedLink]);

  const buildLinkFromResponse = (response: any): string | undefined => {
    const directLink = response?.link || response?.data?.link || response?.url || response?.data?.url;
    if (typeof directLink === "string" && directLink) return directLink;
    const code = response?.code || response?.data?.code;
    return typeof code === "string" && code
      ? `${window.location.origin}/collection/${encodeURIComponent(code)}`
      : undefined;
  };

  const saveSelection = useCallback(async () => {
    setSaving(true);
    setSaveError(undefined);
    setSavedLink(undefined);
    setCopied(false);

    const action: any = await saveHousingsCollection({
      data: { variants: selected.map((item) => ({ ...item })) },
      userId: user?.id,
    });

    if (action?.meta?.requestStatus === "fulfilled") {
      const link = buildLinkFromResponse(action.payload);
      if (link) {
        setSavedLink(link);
        setActiveView("picked");
        try {
          await navigator.clipboard.writeText(link);
          setCopied(true);
        } catch {
          setCopied(false);
        }
      } else {
        setSaveError("Ссылка не получена от сервера");
      }
    } else {
      const apiError = action?.payload ?? action?.error;
      setSaveError(typeof apiError === "string" ? apiError : apiError?.message || "Не удалось сохранить подборку");
    }
    setSaving(false);
  }, [saveHousingsCollection, selected, user?.id]);

  const onCopyLink = useCallback(async () => {
    if (!savedLink) return;
    try {
      await navigator.clipboard.writeText(savedLink);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }, [savedLink]);

  const [loadMoreRef, loadMoreInView] = useInView<HTMLDivElement>("240px");
  const searchValue = isListening ? transcript : query;
  const isInitialLoading = loading && list.length === 0;

  useEffect(() => {
    if (activeView !== "all" || !loadMoreInView || loadingMore || !hasMore || !searchValue.trim()) return;
    doFetch(searchValue.trim(), page + 1, true);
  }, [activeView, doFetch, hasMore, loadMoreInView, loadingMore, page, searchValue]);

  return (
    <div className="ai-search-page">
      <header className="ai-search-header">
        <button type="button" className="ai-search-brand" onClick={() => setActiveView("all")}>
          <span className="ai-search-brand__mark">✦</span>
          <span>ИИ-поиск</span>
        </button>
        <button
          type="button"
          className={`ai-selection-trigger ${selected.length ? "has-items" : ""}`}
          onClick={() => setActiveView("picked")}
        >
          Подборка <span>{selected.length}/{MAX_SELECTED}</span>
        </button>
      </header>

      <main className="ai-search-content">
        {activeView === "picked" ? (
          <section className="ai-selection-view">
            <div className="ai-section-heading">
              <button type="button" className="ai-back-button" onClick={() => setActiveView("all")}>← К поиску</button>
              <h1>Подборка для клиента</h1>
              <p>До трёх вариантов — сохраните и отправьте ссылку, когда всё готово.</p>
            </div>
            <SelectionView
              items={selected}
              onRemove={removeVariant}
              onSave={saveSelection}
              saving={saving}
              savedLink={savedLink}
              onCopyLink={onCopyLink}
              error={saveError}
            />
            {copied && <p className="ai-copy-confirmation">Ссылка скопирована в буфер обмена.</p>}
          </section>
        ) : (
          <>
            <section className={`ai-assistant ${hasSearched ? "has-search" : ""}`} aria-labelledby="ai-search-title">
              <div className="ai-assistant__avatar" aria-hidden="true">✦</div>
              <div className="ai-assistant__copy">
                <span className="ai-assistant__eyebrow">Ассистент по новостройкам</span>
                <h1 id="ai-search-title">{hasSearched ? "Продолжим подбор" : "Расскажите, какую квартиру ищете"}</h1>
                <p>{hasSearched ? "Уточните запрос — я обновлю рекомендации." : "Понимаю обычную речь: бюджет, районы, сроки сдачи, рассрочку и состав семьи."}</p>
              </div>

              {!hasSearched && (
                <div className="ai-suggestions" aria-label="Примеры запросов">
                  {SUGGESTIONS.map((suggestion) => (
                    <button type="button" key={suggestion} onClick={() => handleSearch(suggestion)}>{suggestion}</button>
                  ))}
                </div>
              )}

              <div className="ai-composer">
                <textarea
                  value={searchValue}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      handleSearch();
                    }
                  }}
                  placeholder="Например: двухкомнатная до 8 млн рядом с метро"
                  aria-label="Запрос для ИИ-поиска"
                />
                <button
                  type="button"
                  className={`ai-composer__voice ${isListening ? "is-listening" : ""}`}
                  onClick={onVoiceToggle}
                  aria-label={isListening ? "Остановить голосовой ввод" : "Ввести запрос голосом"}
                  aria-pressed={isListening}
                >
                  <MicIcon />
                </button>
                <button
                  type="button"
                  className="ai-composer__submit"
                  onClick={() => handleSearch()}
                  disabled={!searchValue.trim()}
                >
                  <span>Найти</span><b>↑</b>
                </button>
              </div>
              {isListening && <div className="ai-listening" role="status">Слушаю запрос…</div>}
            </section>

            {isInitialLoading && (
              <div className="ai-response ai-response--loading" role="status">
                <span className="ai-loading-dots"><i /><i /><i /></span> Подбираю варианты по вашему запросу
              </div>
            )}

            {error && (
              <div className="ai-search-error"><Error title="Не получилось подобрать варианты" description="Попробуйте сформулировать запрос иначе или повторите позже." /></div>
            )}

            {!isInitialLoading && !error && hasSearched && (
              <section className="ai-results" aria-live="polite">
                <div className="ai-response">
                  <div className="ai-response__mark">✦</div>
                  <div>
                    <strong>{`Подобрал ${list.length} ${list.length === 1 ? "жилой комплекс" : "жилых комплексов"}`}</strong>
                    <p>Каждая карточка — отдельный ЖК. Откройте её, чтобы сравнить все доступные варианты.</p>
                  </div>
                </div>

                {list.length ? (
                  <ul className="ai-results-list">
                    {list.map((item) => (
                      <HousingCard
                        key={item.id}
                        item={item}
                        onOpenVariants={setActiveHousing}
                        selectedVariantsCount={selectedVariantsByHousing.get(item.id) ?? 0}
                      />
                    ))}
                  </ul>
                ) : (
                  <div className="ai-empty-results">Не нашёл точных совпадений. Попробуйте убрать одно из условий.</div>
                )}

                {(loadingMore || hasMore) && (
                  <div ref={loadMoreRef} className="ai-load-more">
                    {loadingMore ? "Ищу ещё варианты…" : "Прокрутите ниже, чтобы увидеть ещё варианты"}
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </main>
      <VariantsDrawer
        housing={activeHousing}
        selectedIds={selectedIds}
        disabledByLimit={disabledByLimit}
        onToggleVariant={onToggleVariant}
        onClose={() => setActiveHousing(null)}
      />
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  data: selectAISearchData(state),
  filters: selectAISearchFilters(state),
  loading: selectAISearchLoading(state),
  error: selectAISearchError(state),
  message: selectAISearchMessage(state),
  user: selectUserInfo(state),
});

const mapDispatchToProps = {
  fetchAISearchResults: AISearchActionCreator.fetchAISearchResults,
  saveHousingsCollection: AISearchActionCreator.saveHousingsCollection,
};

export const AISearch = connect(mapStateToProps, mapDispatchToProps)(_AISearch);
